import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  ACCEPTED_ALL_CONSENT,
  DEFAULT_CONSENT,
  DENIED_OPTIONAL_CONSENT,
  readConsentSettings,
  writeConsentSettings,
  type ConsentSettings,
} from "@/utils/consent";
import { useLocation } from "react-router-dom";

const translations = {
  en: {
    bannerText:
      'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies. You can also continue with necessary cookies only — we still measure visits without profiling.',
    cookieSettings: "Cookie Settings",
    acceptAll: "Accept All",
    rejectOptional: "Necessary only",
    preferences: {
      title: "Cookie Preferences",
      description:
        "Customize your cookie preferences. Some cookies are necessary for the website to function and cannot be disabled. If you decline analytics/marketing, we still count visits without storing profiling cookies (Google Consent Mode).",
      necessary: {
        title: "Necessary Cookies",
        description: "Required for the website to function properly.",
      },
      analytics: {
        title: "Analytics Cookies",
        description: "Help us understand how visitors interact with our website.",
      },
      marketing: {
        title: "Marketing Cookies",
        description: "Used to deliver personalized ads and content via Meta.",
      },
    },
    cancel: "Cancel",
    savePreferences: "Save Preferences",
  },
  bg: {
    bannerText:
      'Използваме бисквитки, за да подобрим вашето сърфиране, да предоставяме персонализирано съдържание и да анализираме трафика ни. С „Приеми Всички“ се съгласявате с използването им. Можете и да продължите само с необходимите — тогава броим посещения без профилиране.',
    cookieSettings: "Настройки на Бисквитките",
    acceptAll: "Приеми Всички",
    rejectOptional: "Само необходими",
    preferences: {
      title: "Предпочитания за Бисквитки",
      description:
        "Персонализирайте предпочитанията си. Някои бисквитки са задължителни. При отказ от аналитични/маркетингови все пак броим посещения без профилиращи бисквитки (Google Consent Mode).",
      necessary: {
        title: "Необходими Бисквитки",
        description: "Задължителни за правилното функциониране на уебсайта.",
      },
      analytics: {
        title: "Аналитични Бисквитки",
        description: "Помагат ни да разберем как посетителите взаимодействат с нашия уебсайт.",
      },
      marketing: {
        title: "Маркетингови Бисквитки",
        description: "Използват се за показване на персонализирани реклами и съдържание чрез Meta.",
      },
    },
    cancel: "Отказ",
    savePreferences: "Запази Предпочитанията",
  },
  de: {
    bannerText:
      "Wir verwenden Cookies, um Ihr Nutzungserlebnis zu verbessern, personalisierte Inhalte bereitzustellen und den Datenverkehr zu analysieren. Mit „Alle akzeptieren“ stimmen Sie zu. Sie können auch nur notwendige Cookies erlauben — Besuche werden dann ohne Profiling gemessen.",
    cookieSettings: "Cookie-Einstellungen",
    acceptAll: "Alle akzeptieren",
    rejectOptional: "Nur notwendige",
    preferences: {
      title: "Cookie-Einstellungen",
      description:
        "Passen Sie Ihre Cookie-Einstellungen an. Einige Cookies sind erforderlich. Bei Ablehnung von Analyse/Marketing zählen wir Besuche weiterhin ohne Profiling-Cookies (Google Consent Mode).",
      necessary: {
        title: "Notwendige Cookies",
        description: "Erforderlich, damit die Website ordnungsgemäß funktioniert.",
      },
      analytics: {
        title: "Analyse-Cookies",
        description: "Helfen uns zu verstehen, wie Besucher mit unserer Website interagieren.",
      },
      marketing: {
        title: "Marketing-Cookies",
        description:
          "Werden verwendet, um personalisierte Anzeigen und Inhalte über Meta bereitzustellen.",
      },
    },
    cancel: "Abbrechen",
    savePreferences: "Einstellungen speichern",
  },
  gr: {
    bannerText:
      "Χρησιμοποιούμε cookies για καλύτερη εμπειρία, εξατομικευμένο περιεχόμενο και ανάλυση επισκεψιμότητας. Με «Αποδοχή όλων» συναινείτε. Μπορείτε να συνεχίσετε μόνο με τα απαραίτητα — μετράμε επισκέψεις χωρίς προφίλ.",
    cookieSettings: "Ρυθμίσεις cookies",
    acceptAll: "Αποδοχή όλων",
    rejectOptional: "Μόνο απαραίτητα",
    preferences: {
      title: "Προτιμήσεις cookies",
      description:
        "Προσαρμόστε τις προτιμήσεις σας. Ορισμένα cookies είναι απαραίτητα. Αν απορρίψετε analytics/marketing, μετράμε επισκέψεις χωρίς cookies προφίλ (Google Consent Mode).",
      necessary: {
        title: "Απαραίτητα cookies",
        description: "Απαιτούνται για τη σωστή λειτουργία του ιστότοπου.",
      },
      analytics: {
        title: "Cookies ανάλυσης",
        description: "Μας βοηθούν να κατανοήσουμε πώς οι επισκέπτες χρησιμοποιούν τον ιστότοπο.",
      },
      marketing: {
        title: "Cookies μάρκετινγκ",
        description:
          "Χρησιμοποιούνται για την προβολή εξατομικευμένων διαφημίσεων και περιεχομένου μέσω Meta.",
      },
    },
    cancel: "Ακύρωση",
    savePreferences: "Αποθήκευση προτιμήσεων",
  },
};

const CookieConsent = () => {
  const location = useLocation();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [openedSettingsFromBanner, setOpenedSettingsFromBanner] = useState(false);
  const [settings, setSettings] = useState<ConsentSettings>({
    ...DEFAULT_CONSENT,
  });
  const [language, setLanguage] = useState<"bg" | "en" | "de" | "gr">("bg");
  const t = translations[language];

  useEffect(() => {
    const saved = readConsentSettings();
    if (!saved) {
      setShowBanner(true);
    } else {
      setSettings(saved);
    }
  }, []);

  // Settings UI defaults to all enabled only when there is no saved choice yet.
  useEffect(() => {
    if (!showSettings) return;
    const saved = readConsentSettings();
    if (saved) {
      setSettings(saved);
      return;
    }
    setSettings({ ...ACCEPTED_ALL_CONSENT });
  }, [showSettings]);

  useEffect(() => {
    const match = location.pathname.match(/^\/(bg|en|de|gr)(?=\/|$)/);
    const currentLang = match?.[1];
    if (currentLang === "bg" || currentLang === "en" || currentLang === "de" || currentLang === "gr") {
      setLanguage(currentLang);
      return;
    }
    setLanguage("bg");
  }, [location.pathname]);

  const saveSettings = (newSettings: ConsentSettings) => {
    writeConsentSettings(newSettings);
    setSettings(newSettings);
    setShowBanner(false);
    setShowSettings(false);
    setOpenedSettingsFromBanner(false);
    // GTM consent update + Meta init run in App PageViewTracker on consent:updated.
  };

  const handleAcceptAll = () => {
    saveSettings({ ...ACCEPTED_ALL_CONSENT });
  };

  const handleRejectOptional = () => {
    saveSettings({ ...DENIED_OPTIONAL_CONSENT });
  };

  const openSettingsFromBanner = () => {
    setOpenedSettingsFromBanner(true);
    setShowBanner(false);
    setShowSettings(true);
  };

  const handleSettingsOpenChange = (open: boolean) => {
    setShowSettings(open);
    if (open) return;

    // Cancel / dismiss without saving must restore the banner if no choice was stored yet.
    if (!readConsentSettings() && openedSettingsFromBanner) {
      setShowBanner(true);
    }
    setOpenedSettingsFromBanner(false);
  };

  if (!showBanner && !showSettings) return null;

  return (
    <>
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 bg-white p-4 shadow-lg border-t z-50">
          <div className="container mx-auto max-w-7xl flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm text-gray-600">{t.bannerText}</p>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <Button variant="outline" onClick={openSettingsFromBanner}>
                {t.cookieSettings}
              </Button>
              <Button variant="outline" onClick={handleRejectOptional}>
                {t.rejectOptional}
              </Button>
              <Button onClick={handleAcceptAll}>{t.acceptAll}</Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={showSettings} onOpenChange={handleSettingsOpenChange}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t.preferences.title}</DialogTitle>
            <DialogDescription>{t.preferences.description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>{t.preferences.necessary.title}</Label>
                <p className="text-sm text-muted-foreground">{t.preferences.necessary.description}</p>
              </div>
              <Switch checked disabled />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>{t.preferences.analytics.title}</Label>
                <p className="text-sm text-muted-foreground">{t.preferences.analytics.description}</p>
              </div>
              <Switch
                checked={settings.analytics}
                onCheckedChange={(checked) => setSettings({ ...settings, analytics: checked })}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>{t.preferences.marketing.title}</Label>
                <p className="text-sm text-muted-foreground">{t.preferences.marketing.description}</p>
              </div>
              <Switch
                checked={settings.marketing}
                onCheckedChange={(checked) => setSettings({ ...settings, marketing: checked })}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => handleSettingsOpenChange(false)}>
              {t.cancel}
            </Button>
            <Button onClick={() => saveSettings(settings)}>{t.savePreferences}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
